import { DashedTitle } from '@fasstech/middle-design-system';
import {
  ControlledCity,
  ControlledInput,
  ControlledPhoneInput,
  ControlledPostalCode,
  ControlledResidenceCountry
} from '@@viewCommonComponents/controlled';
import {
  isForeignPhoneNumberValid,
  isMobilePhoneNumberValid,
  isRequiredInput,
  nameIsValid
} from '@@viewHelpers/validations';
import {
  isForeignResidence,
  isFranceCountry,
  pickPhoneIndicatives
} from '@@viewHelpers/residenceCountry.js';
import useFetchCitiesQuery from '@@views/souscriptionInformations/client/hooks/useFetchCitiesQuery.js';
import { isNotNilOrEmpty } from '@@serverHelpers';
import { useEffect, useRef } from 'react';
import { equals, propOr } from 'ramda';

const REPRESENTATIVE_COUNTRY_FIELD = 'representative.addresses.0.country';
const REPRESENTATIVE_POSTAL_CODE_FIELD =
  'representative.addresses.0.zipCode';
const REPRESENTATIVE_CITY_FIELD = 'representative.addresses.0.city';

const SouscriptionInformationsExtraFormRepresentative = ({
  cfeContributionsPaidByRepresentative,
  countries = [],
  form,
  phoneIndicatives = [],
  internationalPhoneIndicatives = [],
  postalCodeFranceMetro,
  postalCodeDROM
}) => {
  const country = form.watch(REPRESENTATIVE_COUNTRY_FIELD);
  const postalCode = form.watch(REPRESENTATIVE_POSTAL_CODE_FIELD);
  const countryIsSelected = isNotNilOrEmpty(country);
  const displayFrenchAddressFields =
    countryIsSelected && isFranceCountry(country);
  const displayForeignAddressFields =
    countryIsSelected && !isFranceCountry(country);
  const representativePhoneIndicatives = pickPhoneIndicatives(country, {
    french: phoneIndicatives,
    international: internationalPhoneIndicatives
  });
  const previousCountry = useRef(country);

  useEffect(() => {
    if (equals(previousCountry.current, country)) return;

    previousCountry.current = country;
    form.setValue('representative.phones.0.internationalCode', '');
  }, [country, form]);

  const { fetchCities: cities } = useFetchCitiesQuery({
    variables: { postalCode },
    skip: !displayFrenchAddressFields || !postalCode
  });

  if (!cfeContributionsPaidByRepresentative) return null;

  return (
    <>
      <DashedTitle
        className="col-span-2"
        data-testid="CFE_REPRESENTATIVE_INFORMATIONS"
      >
        Coordonnées du mandataire
      </DashedTitle>
      <ControlledInput
        name="representative.lastName"
        label="Nom du mandataire"
        form={form}
        rules={nameIsValid(true)}
        displayErrorMessage
        data-testid="CFE_REPRESENTATIVE_NAME"
      />
      <ControlledInput
        name="representative.addresses.0.fields.0"
        label="Adresse"
        form={form}
        rules={isRequiredInput(true)}
        displayErrorMessage
        data-testid="CFE_REPRESENTATIVE_ADDRESS"
      />
      <ControlledResidenceCountry
        name={REPRESENTATIVE_COUNTRY_FIELD}
        label="Pays"
        form={form}
        countries={countries}
        isRequired
        dataTestId="CFE_REPRESENTATIVE_COUNTRY"
      />
      <ControlledPostalCode
        name={REPRESENTATIVE_POSTAL_CODE_FIELD}
        postalCodeLabel="Code postal"
        internationalZipCodeLabel="Code ZIP"
        form={form}
        isDisplayed={countryIsSelected}
        isRequired
        displayFrenchFields={displayFrenchAddressFields}
        displayForeignFields={displayForeignAddressFields}
        postalCodeFranceMetro={postalCodeFranceMetro}
        postalCodeDROM={postalCodeDROM}
        displayErrorMessage
        postalCodeTestId="CFE_REPRESENTATIVE_ZIP_CODE"
        internationalZipCodeTestId="CFE_REPRESENTATIVE_ZIP_CODE"
      />
      <ControlledCity
        name={REPRESENTATIVE_CITY_FIELD}
        label="Ville"
        form={form}
        cities={propOr([], 'cities', cities)}
        isDisplayed={countryIsSelected}
        isRequired
        displayFrenchFields={displayFrenchAddressFields}
        displayForeignFields={displayForeignAddressFields}
        cityTestId="CFE_REPRESENTATIVE_CITY"
        foreignCityTestId="CFE_REPRESENTATIVE_CITY"
      />
      <ControlledPhoneInput
        name="representative.phones.0"
        label="Téléphone"
        form={form}
        rules={
          isForeignResidence(country)
            ? isForeignPhoneNumberValid(true)
            : isMobilePhoneNumberValid(true)
        }
        options={representativePhoneIndicatives}
        isDisplayed={countryIsSelected}
        isInternational
        data-testid="CFE_REPRESENTATIVE_PHONE"
      />
    </>
  );
};

export default SouscriptionInformationsExtraFormRepresentative;
